import { formatDate } from "@/utils/date";
import { Link as BiomeLink, Box, ShimmerBox, Stack } from "@biom3/react";
import { useFlags } from "launchdarkly-react-client-sdk";
import Link from "next/link";
import { useMemo } from "react";
import { Header } from "../TilesSection/Header";
import { Eligibility } from "./Eligibility/Eligibility";
import { ImxFoundation } from "./ImxFoundation";
import { JackpotTable } from "./JackpotTable";
import { RarityText } from "./RarityText";
import { FlaggedAccountMessage } from "./Redemption/FlaggedAccountMessage";
import { RedemptionPanel } from "./RedemptionPanel";
import { RedemptionRewards } from "./RedemptionRewards";
import { RedemptionRewardsBackground } from "./RedemptionRewardsBackground";
import { RewardKeysAnimation } from "./RewardKeysAnimation";
import { RewardsBanner } from "./RewardsBanner";
import { TranslucentContainer } from "./TranslucentContainer";
import { WeeklyRewardsDevPanel } from "./WeeklyRewardsDevPanel";
import {
  type WeeklyRewardsPeriod,
  useWeeklyRewards,
} from "./WeeklyRewardsProvider";
import { imxFormatter } from "./tiers/config";

export const WeeklyRewards = () => {
  const { weeklyRewardsDevPanelEnabled } = useFlags();

  const {
    eligibility,
    currentPeriod,
    isTicketsLoading,
    sweepstakeTickets,
    sweepstake,
    isAccountFlagged,
    latestClaimedTicket,
    jackpotWinners,
    isLoading,
    isClaiming,
    showPrizeModal,
    showRedemptionModal,
    prizeConfig,
    ticketUnlockDate,
    isTicketFromCurrentSweepstake,
    currentRarity,
  } = useWeeklyRewards();

  const isEligible = eligibility?.is_eligible;

  const isThanksPeriod = currentPeriod === "THANKS";
  const defaultThirtyDays = new Date(
    new Date().setDate(new Date().getDate() + 30),
  );
  const claimableTicket = sweepstakeTickets?.find(
    (ticket) => ticket.status === "claimable",
  );

  const timeUntilExpiration = formatDate(
    new Date(claimableTicket?.expires_at ?? defaultThirtyDays),
    false,
  );

  const nextWeeklyRunDate = sweepstake?.next?.startDate
    ? new Date(sweepstake?.next.startDate)
    : new Date(Date.now() + 3 * 24 * 60 * 60 * 1000);

  const nextWeeklyRunDateFormatted = formatDate(nextWeeklyRunDate, false);
  const ticketUnlockDateFormatted = ticketUnlockDate
    ? formatDate(new Date(ticketUnlockDate), false)
    : undefined;
  const hasWonIMX = eligibility?.rules.has_valuable_key;

  const hasWonMythicReward = latestClaimedTicket?.metadata?.some(
    (reward) =>
      reward.type === "token" &&
      (reward.metadata.reward_source === "jackpot" ||
        reward.metadata.is_jackpot),
  );
  const hasWonNonJackpotIMX = latestClaimedTicket?.metadata?.some(
    (reward) =>
      reward.type === "token" &&
      !(
        reward.metadata.reward_source === "jackpot" ||
        reward.metadata.is_jackpot
      ),
  );

  const thanksTitle = hasWonMythicReward
    ? "Mythic Reward secured!"
    : hasWonNonJackpotIMX
      ? "Congratulations!"
      : "Your rewards are in.";

  const thanksSubtitle = hasWonMythicReward
    ? ""
    : hasWonNonJackpotIMX
      ? "Your rewards for this week."
      : "";

  const thanksDescription =
    hasWonMythicReward || hasWonNonJackpotIMX ? (
      <>
        IMX may take up to 2 hours to arrive in your wallet. If it hasn't
        arrived by then,{" "}
        <Link
          href="https://support.immutable.com/"
          target="_blank"
          rel="noreferrer"
          style={{ textDecoration: "none" }}
        >
          contact support
        </Link>
        .
      </>
    ) : (
      `You may not have won IMX this week — but every Key gets you closer. Come back ${nextWeeklyRunDateFormatted} for the next cycle.`
    );

  const title = useMemo(() => {
    const qualifiedTitle = (
      <>
        You've qualified. <br />
        Redeem soon.
      </>
    );

    const fallbackTitle = "Embark on Game Quests to Qualify for Weekly Rewards";

    const titleForPeriod: Record<WeeklyRewardsPeriod, React.ReactNode> = {
      THANKS: (
        <>
          {thanksTitle}
          {thanksSubtitle && <br />}
          {thanksSubtitle}
        </>
      ),
      CLAIM:
        hasWonIMX && !isEligible ? (
          <> You've got IMX — Don’t Stop Now!</>
        ) : (
          <>
            Ready to see what your <RarityText rarity={currentRarity} /> Key
            unlocks?
          </>
        ),
      CALCULATE: currentRarity ? (
        <>
          You got a <RarityText rarity={currentRarity} /> Key.
        </>
      ) : (
        "Rewards is brewing. Come back soon."
      ),
      REDEEM: (
        <>
          Get a Key. Claim Rewards.
          <br />
          Will yours be Mythic?
        </>
      ),
      IDLE: isEligible ? qualifiedTitle : fallbackTitle,
    };
    return titleForPeriod[currentPeriod];
  }, [
    currentPeriod,
    isEligible,
    currentRarity,
    thanksTitle,
    thanksSubtitle,
    hasWonIMX,
  ]);

  // Show eligibility screen when user has won IMX but hasn't completed eligibility requirements
  const shouldShowEligibilityCheck =
    currentPeriod === "CLAIM" && hasWonIMX && !isEligible;

  const description = useMemo(() => {
    const fallbackDescription = (
      <>
        In the Beta version of the Weekly Rewards Program, redeem your Gems for
        Keys and get the chance to receive rewards like IMX, NFTs and more.{" "}
        <Link href="/quests/#main-quests" style={{ textDecoration: "none" }}>
          Complete additional eligible quests
        </Link>{" "}
        to qualify for even greater rewards.
      </>
    );

    const descriptionForPeriod: Record<WeeklyRewardsPeriod, React.ReactNode> = {
      THANKS: thanksDescription,
      CLAIM: shouldShowEligibilityCheck
        ? "Link a social + verify your number, and your weekly draw rewards are all yours."
        : `Unlock your rewards and find out if you've won. Unlock by ${timeUntilExpiration} or rewards expire!`,
      CALCULATE: `${isTicketFromCurrentSweepstake ? "Continue questing to reach higher tiers! " : ""}Your reward will be ready to unlock on ${ticketUnlockDateFormatted}. Check back to unlock it and see what you've received.`,
      REDEEM: (
        <>
          {sweepstake?.current?.isLiveProgressKey && (
            <>
              Claim your Key now and continue questing to reach higher tiers!{" "}
            </>
          )}
          At the end of each weekly cycle, your Key can unlock rewards like IMX,
          Gems, Tier-Ups, and Free Entry.{" "}
          {!sweepstake?.current?.isLiveProgressKey && (
            <>
              The prize pool totals{" "}
              <strong>{imxFormatter.format(prizeConfig.totalImx)} IMX</strong>,
              with some Mythic Rewards worth up to{" "}
              <strong>{imxFormatter.format(prizeConfig.maxMythic)} IMX</strong>.{" "}
            </>
          )}
          <Link
            href="/rewards/tiers/"
            target="_blank"
            rel="noreferrer"
            style={{ textDecoration: "none" }}
          >
            Learn more.
          </Link>
        </>
      ),
      IDLE: fallbackDescription,
    };
    return descriptionForPeriod[currentPeriod];
  }, [
    prizeConfig,
    currentPeriod,
    thanksDescription,
    timeUntilExpiration,
    sweepstake?.current?.isLiveProgressKey,
    shouldShowEligibilityCheck,
    ticketUnlockDateFormatted,
    isTicketFromCurrentSweepstake,
  ]);

  if (isLoading || isClaiming || showPrizeModal || showRedemptionModal) {
    return (
      <TranslucentContainer>
        <ShimmerBox
          sx={{
            width: "100%",
            height: "50px",
          }}
        />
        <ShimmerBox
          sx={{
            width: "100%",
            height: "200px",
          }}
        />
        <ShimmerBox
          sx={{
            width: "100%",
            height: "200px",
          }}
        />
      </TranslucentContainer>
    );
  }

  return (
    <>
      <TranslucentContainer>
        <Stack
          direction="column"
          sx={{
            minHeight: "500px",
            height: "100%",
          }}
          gap="base.spacing.x12"
        >
          <Box sx={{ flex: 1 }}>
            <RewardsBanner
              sweepstakeName={
                sweepstake?.current?.questName ||
                sweepstake?.previous?.questName
              }
              title={title}
              isLoading={isTicketsLoading}
              description={description}
            />
            {isThanksPeriod && <RedemptionRewardsBackground />}
            {isAccountFlagged && <FlaggedAccountMessage />}
            {shouldShowEligibilityCheck && <Eligibility />}
            <RedemptionPanel />
            {!isThanksPeriod && !showRedemptionModal && <RewardKeysAnimation />}
          </Box>
          <ImxFoundation />
        </Stack>
      </TranslucentContainer>

      {latestClaimedTicket && !isThanksPeriod && (
        <Box
          sx={{
            mb: "base.spacing.x12",
          }}
        >
          <Header
            showDivider={true}
            title={"Your Latest Rewards"}
            description={
              latestClaimedTicket.quest_info
                ? `${latestClaimedTicket.quest_info.name} (${new Date(
                    latestClaimedTicket.quest_info.start_date,
                  ).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                    timeZone: "UTC",
                  })} - ${new Date(
                    latestClaimedTicket.quest_info.end_date,
                  ).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                    timeZone: "UTC",
                  })})`
                : undefined
            }
            testId="last_rewards"
            action={
              <BiomeLink
                rc={<a href="/rewards/history" />}
                variant="primary"
                size="small"
                sx={{ whiteSpace: "nowrap" }}
              >
                View rewards history
              </BiomeLink>
            }
          />
          <RedemptionRewards claimedRewards={latestClaimedTicket.metadata} />
        </Box>
      )}

      {jackpotWinners?.[0] && <JackpotTable jackpotWinners={jackpotWinners} />}

      {weeklyRewardsDevPanelEnabled && <WeeklyRewardsDevPanel />}
    </>
  );
};
